import type { Patient } from "./Patient";
import type { Medicine, PrescriptionType } from "./Search";

export enum RequestStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  SIGNING = "SIGNING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  SIGNING_ERROR = "SIGNING_ERROR",
}

export const StatusTranslation: Record<RequestStatus, string> = {
  DRAFT: "Borrador",
  PENDING: "Pendiente",
  SIGNING: "Firmando",
  ACCEPTED: "Emitida",
  REJECTED: "Rechazada",
  SIGNING_ERROR: "Error de firma",
};

export const StatusPerRequestType: Record<RequestType, Record<RequestStatus, string>> = {
  PRESCRIPTION: { ...StatusTranslation },
  COMPOUNDED_PRESCRIPTION: { ...StatusTranslation },
  MEDICAL_CERTIFICATE: {
    ...StatusTranslation,
    ACCEPTED: "Emitido",
    REJECTED: "Rechazado",
  },
  MEDICAL_ORDER: { ...StatusTranslation },
};

export enum RequestType {
  PRESCRIPTION = "PRESCRIPTION",
  COMPOUNDED_PRESCRIPTION = "COMPOUNDED_PRESCRIPTION",
  MEDICAL_ORDER = "MEDICAL_ORDER",
  MEDICAL_CERTIFICATE = "MEDICAL_CERTIFICATE",
}

export const requestTypeExtendedTranslation: Record<RequestType, string> = {
  [RequestType.PRESCRIPTION]: "Receta médica",
  [RequestType.COMPOUNDED_PRESCRIPTION]: "Receta magistral",
  [RequestType.MEDICAL_ORDER]: "Orden médica",
  [RequestType.MEDICAL_CERTIFICATE]: "Certificado médico",
};

export const requestTypeTranslation: Record<RequestType, string> = {
  [RequestType.PRESCRIPTION]: "Receta",
  [RequestType.COMPOUNDED_PRESCRIPTION]: "Receta magistral",
  [RequestType.MEDICAL_ORDER]: "Orden médica",
  [RequestType.MEDICAL_CERTIFICATE]: "Certificado médico",
};

export const requestTypeSimplifiedTranslation: Record<RequestType, string> = {
  [RequestType.PRESCRIPTION]: "Receta",
  [RequestType.COMPOUNDED_PRESCRIPTION]: "Receta magistral",
  [RequestType.MEDICAL_ORDER]: "Orden",
  [RequestType.MEDICAL_CERTIFICATE]: "Certificado",
};

export const requestTypeNavigateParser: Record<RequestType, string> = {
  [RequestType.PRESCRIPTION]: "receta",
  [RequestType.COMPOUNDED_PRESCRIPTION]: "receta-magistral",
  [RequestType.MEDICAL_ORDER]: "orden-medica",
  [RequestType.MEDICAL_CERTIFICATE]: "certificado",
};

export const requestTypeFromUrlParser: Record<string, RequestType> = {
  receta: RequestType.PRESCRIPTION,
  "receta-magistral": RequestType.COMPOUNDED_PRESCRIPTION,
  "orden-medica": RequestType.MEDICAL_ORDER,
  certificado: RequestType.MEDICAL_CERTIFICATE,
};

export interface Request {
  id: string;
  doctorCode: string;
  status: RequestStatus;
  patientUuid: string;
  details?: RequestDetail[];
  attachments?: RequestAttachment[];
  requestedAt: number;
  rejectedAt?: number;
  patient: Patient;
  comment: string;
  indications: string;
  documentType: RequestType;
  managementSummary?: string;
}

export interface RequestDetail {
  id: string;
  requestId: string;

  /** Usually medicine (product) name, except for generics where is empty */
  name?: string;
  activePrinciple: string;
  /** Missing for manual prescriptions */
  concentration?: string;
  /** Missing for manual prescriptions and generics */
  laboratory?: string;
  /** Missing for manual prescriptions and generics */
  quantity?: number;
  /** Missing for manual prescriptions */
  unit?: string;

  posologyQuantity: number;
  posologyFormat: string;
  frequencyQuantity: number;
  frequencyPeriod: string;
  consumptionType: string;
  durationQuantity: number;
  durationPeriod: string;
  /** This is only needed for HELD prescriptionTypes */
  quantityToDispense: number | undefined;
  indications: string;
  prescriptionType?: PrescriptionType;
  /** Only available in medicine products (searched via Meki) */
  slug?: string;
  /** Only available in medicine products (searched via Meki) */
  medicine?: Medicine;
}

export interface HistoricRequestCardDetails {
  id?: string;
  requestId?: string;

  name?: string;
  activePrinciple?: string;
  concentration?: string;
  laboratory?: string;

  compoundedIngredients?: string;

  posologyQuantity: number;
  posologyFormat: string;
  frequencyQuantity: number;
  frequencyPeriod: string;
  durationQuantity: number;
  durationPeriod: string;
  consumptionType?: string;
  quantityToDispense?: number;
  indications?: string;
}

export interface RequestAttachment {
  id: string;
  name: string;
  requestId: string;
  url: string;
  createdAt: number;
  type?: string;
}

export interface RequestCreationPayload {
  documentType: RequestType;
  doctorCode: string;
  status: RequestStatus;
  comment: string;
}

export interface NewRequestPayload {
  doctorCode: string;
  patient: Patient;
  request: RequestCreationPayload;
  uploadedFiles?: File[];
}

export interface NewRequestResponse {
  request: Request;
}
